import { supabase } from '../../shared/supabase/supabaseClient';
import { addSentryBreadcrumb } from '../../shared/sentry/sentryUser';
import { formatMoney } from '../../shared/format/public-api';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { buildPaymentAllocations } from './customerPayments';

export type CustomerPaymentIntentResult = {
  intentId: string;
  movementId: string | null;
  status: string;
};

/**
 * Crea el intent de cobro QR (Customers:Cobrar → Mercado Pago QR).
 *
 * El intent guarda el payload completo del cobro (amount + saleId puntual o
 * allocations FIFO/parciales calculadas acá — misma lógica que efectivo).
 * No crea movimiento ni toca balance: eso lo hace settle_customer_payment_intent
 * cuando MP confirma el pago (webhook o polling EF).
 */
export const createCustomerPaymentIntent = async (params: {
  customerId: string;
  amount: number;
  note?: string;
  branchId?: string | null;
  saleId?: string | null;
  performedByUserId?: string | null;
  originDeviceId?: string | null;
  movementId?: string | null;
}): Promise<CustomerPaymentIntentResult> => {
  const movementId = params.movementId?.trim() || crypto.randomUUID();
  const allocationRows = await buildPaymentAllocations(params.customerId, params.amount, params.saleId);

  const payload: Record<string, unknown> = {
    customerId: params.customerId,
    amount: params.amount,
    note: params.note,
    branchId: params.branchId ?? undefined,
    saleId: params.saleId ?? undefined,
    movementId,
    // operationId = movementId: un retry tras timeout no debe crear un segundo
    // intent (dos QR pagables → el segundo liquidaría contra deuda inexistente).
    operationId: movementId,
    performedByUserId: params.performedByUserId ?? null,
    originDeviceId: params.originDeviceId ?? null,
    allocations: allocationRows.map((row) => ({
      id: row.id,
      saleId: row.saleId,
      amount: row.amount,
      createdAt: row.createdAt,
    })),
  };

  addSentryBreadcrumb('customers', `Intent cobro QR: ${formatMoney(params.amount)}`, 'info', {
    customerId: params.customerId,
    amount: params.amount,
    saleId: params.saleId ?? null,
  });

  const { data, error } = await supabase.schema('customers').rpc('apply_customer_payment_intent_command', {
    p_operation: 'create',
    p_payload: payload,
  });
  if (error) {
    const err = new Error(error.message || 'Error al crear el cobro QR');
    captureOpsFailure({
      module: 'customers',
      op: 'customer.payment_intent.create',
      error: err,
      ingestError: error.message,
      applied: false,
      path: 'web-ram',
      itemId: params.customerId,
      extra: { customerId: params.customerId },
    });
    throw err;
  }
  const result = (data ?? {}) as { intent_id?: string; movement_id?: string; status?: string; error?: string };
  if (!result.intent_id) {
    throw new Error(result.error || 'No se pudo crear el cobro QR');
  }
  return { intentId: result.intent_id, movementId: result.movement_id ?? null, status: result.status ?? 'pending' };
};

/** Cancela un intent pendiente (el usuario abandonó el cobro). Idempotente. */
export const cancelCustomerPaymentIntent = async (intentId: string): Promise<void> => {
  const { error } = await supabase.schema('customers').rpc('apply_customer_payment_intent_command', {
    p_operation: 'cancel',
    p_payload: { intentId, operationId: crypto.randomUUID() },
  });
  if (error) throw new Error(error.message || 'No se pudo cancelar el cobro QR');
};
